import { Autocomplete } from "@mui/lab";
import { CircularProgress, Grid2, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { ErrorModal } from "../../components/modals/error-modal";
import { getMomentTz } from "../../services";
import { ShiftData } from "../shift-navigator-v2";
import {
	getShiftsAroundCurrent,
	getShiftsByRange,
} from "./repositories/ShiftRepository";

const moment = getMomentTz();

export const useShiftsAroundCurrent = ({
	currentShiftId,
	shiftsRange,
	enabled = true,
}: {
	currentShiftId?: number;
	shiftsRange?: number;
	enabled?: boolean;
}) => {
	return useQuery<ShiftData[], Error>({
		queryKey: ["shiftsAroundCurrent", currentShiftId, shiftsRange],
		queryFn: ({ signal }) =>
			getShiftsAroundCurrent({ currentShiftId, shiftsRange }),
		enabled,
	});
};

export const useShiftsByRange = ({
	shiftId,
	assetId,
	shiftsBefore,
	shiftsAfter,
	enabled = true,
}: {
	shiftId?: number;
	assetId?: number;
	shiftsBefore?: number;
	shiftsAfter?: number;
	enabled?: boolean;
}) => {
	return useQuery<ShiftData[], Error>({
		queryKey: ["shiftsByRange", shiftId, assetId, shiftsBefore, shiftsAfter],
		queryFn: () =>
			getShiftsByRange({ shiftId, assetId, shiftsBefore, shiftsAfter }),
		enabled,
	});
};

type Props = {
	currentShiftId: number;
	value: number | null;
	onShiftSelected: (shift: ShiftData | null) => void;
	disabled?: boolean;
	shiftsRange?: number;
	assetId?: number;
	shiftsAfter?: number;
};

export const ShiftAutocomplete = ({
	currentShiftId,
	value,
	onShiftSelected,
	disabled,
	shiftsRange,
	assetId,
	shiftsAfter,
}: Props) => {
	const [error, setError] = useState<string>("");

	const useNewPath = assetId !== undefined;

	// Legacy path (existing consumers)
	const {
		data: legacyRows,
		isLoading: legacyLoading,
		error: legacyError,
		isError: legacyIsError,
	} = useShiftsAroundCurrent({
		currentShiftId,
		shiftsRange,
		enabled: !useNewPath,
	});

	// New path (asset-filtered, count-based)
	const {
		data: rangeRows,
		isLoading: rangeLoading,
		error: rangeError,
		isError: rangeIsError,
	} = useShiftsByRange({
		shiftId: currentShiftId,
		assetId,
		shiftsBefore: shiftsRange ?? 10,
		shiftsAfter: shiftsAfter ?? 0,
		enabled: useNewPath,
	});

	// Unified variables
	const rows = useNewPath ? rangeRows : legacyRows;
	const isLoading = useNewPath ? rangeLoading : legacyLoading;
	const e = useNewPath ? rangeError : legacyError;
	const isError = useNewPath ? rangeIsError : legacyIsError;

	const selectedShift = rows?.find((type) => type.ShiftId === value) || null;

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<Grid2 container>
				<Grid2 size={{ xs: 12 }}>
					<Autocomplete
						loading={isLoading}
						options={rows || []}
						getOptionLabel={(option) => {
							return `${moment
								.utc(option.ProductionDate)
								.format("MM/DD/YYYY")} - ${option.Shift} - ${option.Crew}`;
						}}
						value={selectedShift}
						onChange={(event, newValue) => {
							if (newValue) {
								onShiftSelected(newValue);
							}
						}}
						disabled={disabled}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Shift"
								variant="outlined"
								size="small"
								fullWidth
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{isLoading ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
										</>
									),
								}}
							/>
						)}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
